import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
/** @type {Record<string, number>} */
const STOCK_PRIORITY = { in: 0, preorder: 1, out: 2 };

export default class ResultsList extends PaginatedList {
  /** @type {MutationObserver | undefined} */
  #stockSortObserver;

  /** @type {boolean} */
  #sortingStock = false;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
    this.#sortByStockPriority();
    this.#observeStockSort();
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#stockSortObserver?.disconnect();
  }

  /**
   * Keep in-stock → preorder → out-of-stock when infinite scroll appends pages.
   */
  #observeStockSort() {
    const { grid } = this.refs;
    if (!grid || this.#stockSortObserver) return;

    this.#stockSortObserver = new MutationObserver(() => {
      if (this.#sortingStock) return;
      this.#sortByStockPriority();
    });
    this.#stockSortObserver.observe(grid, { childList: true });
  }

  #sortByStockPriority() {
    const { grid } = this.refs;
    if (!grid) return;

    const cards = Array.from(grid.querySelectorAll(':scope > [data-pokespott-stock]'));
    if (cards.length < 2) return;

    const sorted = [...cards].sort((a, b) => {
      const aKey = a.getAttribute('data-pokespott-stock') || 'out';
      const bKey = b.getAttribute('data-pokespott-stock') || 'out';
      return (STOCK_PRIORITY[aKey] ?? 9) - (STOCK_PRIORITY[bKey] ?? 9);
    });

    const alreadySorted = sorted.every((card, index) => card === cards[index]);
    if (alreadySorted) return;

    this.#sortingStock = true;
    sorted.forEach((card) => grid.appendChild(card));
    this.#sortingStock = false;
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
